import { getQueryClient } from "@/app/(normal)/clientLayout"
import { TPowerDialerContactList } from "@/schemas/contactList.schema"
import { DialerListSchema } from "@/schemas/user.schema"
import { usePowerDialerStore } from "@/stores/powerDialer/store"
import { syncStore$ } from "@/stores/sync.v2.store"
import type { IRegisterDocumentProps } from "@/sync/syncTypes"
import consola from "consola"

// Track totalCount per list to detect actual changes (not just list switches)
const listTotalCounts = new Map<number, number>()

export const DialerListDocumentHandler = (name: string): IRegisterDocumentProps => ({
  name,
  onFailure: (error: unknown) => {
    consola.error(`Document ${name} failed`, error)
  },
  onRemoved: () => {
    consola.debug(`Document removed: ${name}`)
  },
  onDocumentUpdated: async (data: unknown, rawData: unknown) => {
    // @ts-ignore
    if (Array.isArray((data as any)?.pastCalls) === false) {
      // @ts-ignore
      data.pastCalls = []
    }

    // Sanitize pastCalls - give invalid IDs a negative fallback to prevent Zod coercion errors
    if (Array.isArray((data as any)?.pastCalls)) {
      ;(data as any).pastCalls = (data as any).pastCalls.map((item: any) => {
        if (item && (item.id == null || isNaN(Number(item.id)))) {
          consola.warn(`pastCall item has invalid id: ${item.id}, using fallback`)
          return { ...item, id: -Math.floor(Math.random() * 1000000) }
        }
        return item
      })
    }

    // Sanitize lastCall if it has an invalid ID
    const lastCall = (data as any)?.lastCall
    if (lastCall && typeof lastCall === "object" && Object.keys(lastCall).length > 0) {
      if (lastCall.id == null || isNaN(Number(lastCall.id))) {
        consola.warn(`lastCall has invalid id: ${lastCall.id}, using fallback`)
        ;(data as any).lastCall = { ...lastCall, id: -Math.floor(Math.random() * 1000000) }
      }
    }

    const parsedItem = DialerListSchema.safeParse(data)
    if (!parsedItem.success) {
      consola.error(`We will ignore the update from ${name} because the data is invalid`, parsedItem.error)
      return
    }

    // Update Zustand store instead of Legend State
    usePowerDialerStore.getState().setDialerListData(parsedItem.data ?? undefined)
    consola.log(`Updated state from ${name}`, parsedItem.data)

    // Update contact list size in cache only when totalCount changes for this specific list
    const newTotalCount = parsedItem.data?.stats?.totalCount
    const selectedListId = syncStore$.userPreferences.powerDialerTargetId.peek()

    if (selectedListId && newTotalCount !== undefined) {
      const previousCount = listTotalCounts.get(selectedListId)

      if (previousCount !== newTotalCount) {
        const queryClient = getQueryClient()
        queryClient.setQueryData<TPowerDialerContactList[]>(["contact-lists-direct"], (oldData) => {
          if (!oldData) return oldData
          return oldData.map((list) => (list.sequenceId === selectedListId ? { ...list, size: newTotalCount } : list))
        })
        consola.debug(`Updated contact list ${selectedListId} size to ${newTotalCount}`)
        listTotalCounts.set(selectedListId, newTotalCount)
      }
    }
  },
})
